import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail, MapPin, ArrowUpRight, ArrowRight, X,
  Facebook, Instagram, Linkedin, MessageCircle, Loader2,
  Building2,
} from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";

const SEDE_LEGALE = "Piazza dei Martiri 30, Napoli (NA), 80121";
const P_IVA = "IT 11070891210";
const EMAIL = "info@bvbusinessconsulting.it";
const PHONE_DISPLAY = "+39 344 757 4610";
const WHATSAPP_NUMBER = "393447574610";

const SOCIAL = [
  { name: "Facebook", href: "https://www.facebook.com/share/18S3X1JBXp/?mibextid=wwXIfr", Icon: Facebook },
  { name: "Instagram", href: "https://www.instagram.com/bv_businessconsulting?igsh=MWo0N2F3NDhmdWp1aQ==", Icon: Instagram },
  { name: "LinkedIn", href: "https://www.linkedin.com/company/bv-business-consulting/", Icon: Linkedin },
];

const NAV_FOOTER = [
  { label: "Home", to: "/" },
  { label: "Blog", to: "/blog" },
  { label: "Servizi", to: "/servizi" },
  { label: "Contatti", to: "/contatti" },
];

export default function Footer({ isHome = false }) {
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  return (
    <footer
      data-testid="site-footer"
      className={`bg-[#0E2E4D] text-[#F5F2EB] mt-32 relative${isHome ? " footer-home" : ""}`}
    >
      <div className="bv-container py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* COL 1 — Brand */}
          <div className="md:col-span-4">
            <Link to="/" aria-label="BV Business Consulting SRLS — Home" className="inline-block">
              <img
                src="/bv-logo-full.png"
                alt="BV Business Consulting SRLS"
                data-testid="footer-logo"
                className="block w-full max-w-[280px] h-auto"
              />
            </Link>
            <p className="mt-4 font-display font-extrabold uppercase text-[15px] tracking-[0.02em] leading-snug max-w-xs">
              Strategia, visione e crescita<br />
              al servizio delle imprese.
            </p>
            <Link
              to="/contatti"
              data-testid="footer-cta-consulenza"
              className="mt-8 inline-flex items-center gap-3 border border-white/40 hover:border-white hover:bg-white hover:text-[#0E2E4D] transition-colors px-6 py-3.5 text-[12px] tracking-[0.20em] uppercase font-display font-bold"
            >
              Richiedi una consulenza <ArrowRight size={14} />
            </Link>
          </div>

          {/* COL 2 — Navigazione */}
          <div className="md:col-span-2">
            <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/50">Navigazione</p>
            <ul className="mt-5 space-y-3 font-display font-semibold text-[13px] tracking-[0.04em] uppercase">
              {NAV_FOOTER.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="link-underline text-white/85 hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* COL 3 — Contatti */}
          <div className="md:col-span-3">
            <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/50">Contatti</p>
            <ul className="mt-5 space-y-4 text-[14px]">
              <li className="flex items-start gap-3">
                <MapPin size={15} className="mt-0.5 text-[#36BFC4] shrink-0" />
                <span className="text-white/85" data-testid="footer-sede">{SEDE_LEGALE}</span>
              </li>
              <li className="flex items-start gap-3 text-white/85" data-testid="footer-piva">
                <Building2 size={15} className="mt-0.5 text-[#36BFC4] shrink-0" />
                <span>P.IVA · {P_IVA}</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={15} className="mt-0.5 text-[#36BFC4] shrink-0" />
                <a href={`mailto:${EMAIL}`} data-testid="footer-email" className="link-underline break-all">
                  {EMAIL}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MessageCircle size={15} className="mt-0.5 text-[#36BFC4] shrink-0" />
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="link-underline"
                >
                  WhatsApp · {PHONE_DISPLAY}
                </a>
              </li>
            </ul>
          </div>

          {/* COL 4 — Social + Extra */}
          <div className="md:col-span-3">
            <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/50">Seguici</p>
            <div className="mt-5 flex items-center gap-3" data-testid="footer-social">
              {SOCIAL.map(({ name, href, Icon }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  data-testid={`footer-social-${name.toLowerCase()}`}
                  className="inline-flex items-center justify-center w-10 h-10 border border-white/30 hover:border-white hover:bg-white hover:text-[#0E2E4D] transition-colors"
                >
                  <Icon size={16} strokeWidth={1.6} />
                </a>
              ))}
            </div>

            <button
              type="button"
              data-testid="footer-newsletter-toggle"
              onClick={() => setNewsletterOpen(true)}
              className="mt-8 w-full inline-flex items-center justify-between gap-3 border border-white/30 hover:border-white px-5 py-3 text-[12px] tracking-[0.18em] uppercase font-display font-bold transition-colors"
            >
              <span className="inline-flex items-center gap-2"><Mail size={14} /> Iscriviti alla newsletter</span>
              <ArrowUpRight size={14} />
            </button>

            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Ciao, vorrei informazioni sui vostri servizi")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-chat-whatsapp"
              className="mt-3 w-full inline-flex items-center justify-between gap-3 bg-[#1F8388] hover:bg-[#2BA8AD] text-white px-5 py-3 text-[12px] tracking-[0.18em] uppercase font-display font-bold transition-colors"
            >
              <span className="inline-flex items-center gap-2"><MessageCircle size={14} /> Chatta con noi</span>
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>

        {/* Barra finale */}
        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="font-mono text-[11px] tracking-[0.18em] text-white/55" data-testid="footer-copyright">
            © {new Date().getFullYear()} BV Business Consulting SRLS · Tutti i diritti riservati
          </p>
          <ul className="flex items-center gap-6 font-mono text-[11px] tracking-[0.18em] text-white/55">
            <li>
              <a
                href="https://www.iubenda.com/privacy-policy/23812780"
                data-testid="footer-privacy"
                className="iubenda-white iubenda-noiframe iubenda-embed link-underline"
                title="Privacy Policy"
              >
                Privacy Policy
              </a>
            </li>
            <li>
              <a
                href="https://www.iubenda.com/privacy-policy/23812780/cookie-policy"
                data-testid="footer-cookie"
                className="iubenda-white iubenda-noiframe iubenda-embed link-underline"
                title="Cookie Policy"
              >
                Cookie Policy
              </a>
            </li>
          </ul>
        </div>
      </div>

      {newsletterOpen && <NewsletterPopup onClose={() => setNewsletterOpen(false)} />}
    </footer>
  );
}

function NewsletterPopup({ onClose }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Inserisci un'email valida.");
      return;
    }
    setLoading(true);
    try {
      await subscribeNewsletter(email.trim());
      toast.success("Iscrizione confermata.");
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Errore. Riprova.";
      toast.error(typeof detail === "string" ? detail : "Errore nell'iscrizione.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="newsletter-popup"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[#061A2E]/75 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-[#F5F2EB] text-[#0E2E4D] p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="newsletter-popup-close"
          aria-label="Chiudi"
          className="absolute top-4 right-4 p-1 text-[#0E2E4D]/60 hover:text-[#0E2E4D]"
        >
          <X size={18} />
        </button>
        <p className="overline" style={{ color: "#2BA8AD" }}>Newsletter BV</p>
        <h3 className="h-display text-2xl md:text-3xl mt-3 leading-tight">
          Bandi, agevolazioni, opportunità · una sola email al mese.
        </h3>
        <p className="mt-3 text-[14px] text-[#0E2E4D]/70">
          Nessuno spam. Solo opportunità concrete per la tua azienda.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-3" data-testid="newsletter-popup-form" noValidate>
          <input
            data-testid="newsletter-popup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="indirizzo@email.it"
            required
            className="w-full bg-white px-4 py-3 text-[15px] border border-[#0E2E4D]/15 focus:border-[#2BA8AD] outline-none"
            autoComplete="email"
          />
          <button
            data-testid="newsletter-popup-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#0E2E4D] text-white px-6 py-4 text-[12px] tracking-[0.20em] uppercase font-display font-bold hover:bg-[#2BA8AD] transition-colors disabled:opacity-60"
          >
            {loading ? <><Loader2 size={14} className="animate-spin" /> Invio…</> : <>Iscriviti</>}
          </button>
        </form>
      </div>
    </div>
  );
}
